<template>
  <div
    :id="event.id"
    ref="eventRef"
    v-clickOutside="cancelEdit"
    class="group relative flex flex-col justify-between rounded-8 p-14 h-full w-full mb-12 min-h-94 bg-primary duration-300 ease-in-out"
    :class="[
      isDarkMode ?
        `border-2 border-grey-500 hover:border-grey-300 transition-border ${isEdit ? '' : 'hover:bg-sidebar'}` :
        `shadow-event hover:shadow-event-hover transition-shadow ${isEdit ? '' : 'hover:bg-grey-450'}`,
      isEdit ? 'cursor-default' : 'cursor-grab drag-handler'
    ]"
  >
    <div v-if="!isEdit" class="no-drag absolute top-0 right-0 px-10 pb-10 pt-5 pr-12 z-50">
      <DropdownModule
        ref="menuRef"
        icon="icon-more-horizontal text-20"
        :width="150"
        append-to-body
        class="md:opacity-0 group-hover:opacity-100 anim-opacity"
        :class="{'opacity-100': showMenu}"
        remove-optional-top-gap
        :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
        is-auto-menu-position
        @click="showMenu = true"
        @close="showMenu = false"
      >
        <div>
          <div class="hover:bg-selection rounded-t-10 group-scope text-15">
            <ButtonModule
              text="Edit"
              :prefix="`icon-edit mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="p-10 w-full"
              @click="toggleEdit"
            />
          </div>
          <div class="hover:bg-selection group-scope text-15 rounded-b-10">
            <ButtonModule
              text="Delete"
              :prefix="`icon-delete mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="p-10 w-full"
              @click="deleteEvent"
            />
          </div>
        </div>
      </DropdownModule>
    </div>

    <EmojiPicker
      v-if="showEmojiPicker"
      ref="emojiPickerRef"
      v-clickOutside="closeEmojiPicker"
      class="top-80 left-0"
      :class="{ '-top-280': isMobile }"
      @selectEmoji="insertEmoji"
      @click.stop
    />
    <div>
      <div v-if="!isEdit" class="font-Inter mb-4 text-15 leading-small font-semibold pr-25 line-clamp-2 select-none md:select-auto">
        {{ localEvent.event.title }}
      </div>
      <input
        v-else
        id="title"
        ref="titleInputRef"
        v-model="localEvent.event.title"
        placeholder="What’s the event?"
        class="font-Inter w-full mb-10 text-15 bg-transparent outline-none font-bold pr-5 leading-small block"
        maxlength="40"
        type="text"
        @blur="onBlur"
        @keypress="onKeyPress"
        @keypress.enter="cancelEdit"
      >

      <p
        v-if="!isEdit && localEvent.event.subTitle"
        class="text-14 mb-10 leading-normal line-clamp-3 select-none md:select-auto"
        :class="isDarkMode ? 'text-grey-250' : 'text-grey-1000'"
      >
        {{ localEvent.event.subTitle }}
      </p>
      <input
        v-if="isEdit"
        id="subTitle"
        ref="subTitleInputRef"
        v-model="localEvent.event.subTitle"
        placeholder="Describe it in more detail."
        class="w-full h-full text-14 bg-transparent outline-none pr-5 -mt-5 block"
        maxlength="100"
        type="text"
        @blur="onBlur"
        @keypress="onKeyPress"
        @keypress.enter="cancelEdit"
      >

      <div v-if="isEdit" class="border-t border-grey-500 mt-5" />

      <div class="flex items-center mb-10 mt-5">
        <Mentions v-if="isEdit" v-model="project" class="w-1/2 pr-5" />
        <div v-else class="w-1/2 mr-5 text-14 text-brand no-drag inline-block leading-tight">
          <router-link v-if="project" :to="url">@{{ project.title }}</router-link>
        </div>

        <SelectedBookModal
          v-if="showSelectBookModal"
          ref="selectedBookModalRef"
          class="w-1/2 -mr-10 ml-5"
          label-font-size="text-13 py-1"
          label-classes="mr-8 text-12 w-18 h-18"
          max-width="max-w-150"
          :books="allBooks"
          :selected-book="selectedBook"
          :is-edit="isEdit"
          @toggleBook="toggleBook"
        />
      </div>
    </div>

    <div class="flex justify-between items-end text-12">
      <div v-if="isEdit">
        <div class="leading-none cursor-pointer flex items-center mb-3 text-14">
          <Calendar
            :current-date="event.showAt"
            :attributes="attributes"
            position="top-20 left-0"
            @dayclick="dayclick"
          >
            <div class="flex items-center cursor-pointer">
              <i class="icon-calendar text-link-color mr-5 flex-shrink-0 text-18" />
              <p class="font-bold flex-shrink-0">{{ computedDay.date }} {{ computedDay.month }}</p>
            </div>
          </Calendar>
        </div>
        <TimeInput :time="time" @setTime="setTime" />
      </div>

      <div v-else class="font-bold flex-shrink-0 mr-5 text-14 select-none md:select-auto">
        <p v-if="time">{{ time }}</p>
        <div v-else>All Day</div>
      </div>

      <div class="flex items-center">
        <Recurring
          v-if="!!event.event.periodValue || isEdit"
          :is-edit="isEdit"
          :period-type="event.event.periodType"
          :period-value="event.event.periodValue"
          class="no-drag"
          @addRecurring="addRecurring"
          @updateRecurring="updateRecurring"
          @deleteRecurring="deleteRecurring"
        />

        <div v-if="!isEdit && hasTags" class="flex -mr-5 text-dark-600">
          <div
            v-for="tag in event.event.eventTags"
            :key="tag.id"
            class="text-11 font-medium flex items-center flex-shrink-0 rounded-8 h-23 min-w-50 px-7 mr-5 lowercase select-none md:select-auto"
            :style="`background-color: #${(tag.color).toString(16)}`"
          >
            <p v-if="!!tag.name" class="text-12">#</p>
            {{ tag.name }}
          </div>
        </div>
        <TagsModal v-if="isEdit" :selected-tags="event.event.eventTags" :tags="tags" @toggleTag="toggleEventTag" @updateTag="updateTag" />
      </div>
    </div>
    <RecurringModal ref="recurringModalRef" event-type="Delete" @submit="confirm" />
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, ref } from 'vue'
import { useStore } from 'vuex'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import RecurringModal from '@/components/confirm/RecurringModal'
import TagsModal from '@/components/week-planner/TagsModal'
import Mentions from '@/components/week-planner/Mentions'
import Recurring from '@/components/week-planner/Recurring'
import TimeInput from '@/components/week-planner/TimeInput'
import EmojiPicker from '@/components/shared/EmojiPicker'
import Calendar from '@/components/week-planner/Calendar'
import SelectedBookModal from '@/components/task-board/SelectedBookModal'

export default defineComponent({
  components: {
    DropdownModule,
    RecurringModal,
    ButtonModule,
    TagsModal,
    Mentions,
    Recurring,
    TimeInput,
    EmojiPicker,
    Calendar,
    SelectedBookModal
  },

  props: {
    event: Object,
    selectedBooks: { type: Array, default: () => [] },
    lastWeekDate: String,
    firstWeekDate: String
  },

  emits: ['focused'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()

    const eventRef = ref(null)
    const titleInputRef = ref(null)
    const subTitleInputRef = ref(null)
    const recurringModalRef = ref(null)
    const selectedBookModalRef = ref(null)

    let timeWasUpdated = false

    const showEmojiPicker = ref(false)
    const showMenu = ref(false)

    const isDarkMode = computed(() => getters.isDarkMode)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isMobile = computed(() => getters.isMobile)
    const team = computed(() => state.team.team)
    const headquarters = computed(() => state.team.headquarters)

    const tags = computed(() => state.planner.tags.filter(tag => tag.bookId === selectedBook.value?.id))
    const books = computed(() => state.book.books.filter(b => !b.isSection))

    const selectedBook = computed(() => books.value.find(b => b.id === props.event.bookId))

    const allBooks = computed(() => {
      const result = books.value
      if (isTeamAccount.value) {
        result.push(headquarters.value)
      }
      return result
    })

    const localEvent = computed({
      get: () => props.event,
      set: value => commit('updateEvent', value)
    })

    const isEdit = ref(!props.event.event.title)

    const temporaryTitle = ref(props.event.event.title || '')
    const temporarySubTitle = ref(props.event.event.subTitle || '')

    const hasTags = computed(() => props.event?.event?.eventTags?.length > 0)

    const computedDay = computed(() => {
      const date = new Date(props.event.showAt).toLocaleString('en-US', { day: '2-digit' })
      const month = new Date(props.event.showAt).toLocaleString('en-US', { month: 'long' })

      return { date, month }
    })

    const attributes = computed(() => {
      return [
        {
          key: 'today',
          highlight: {
            contentClass: 'text-link-color anim-color',
            contentStyle: { fontWeight: 700 }
          },
          bar: {
            style: {
              backgroundColor: ''
            },
            class: 'bar-today'
          },
          dates: new Date()
        },
        {
          key: 'currentDay',
          highlight: {
            contentClass: 'text-app-color anim-color',
            contentStyle: { fontWeight: 700, backgroundColor: isDarkMode.value ? '#333' : 'rgba(0,0,0,0.04)' }
          },
          dates: new Date(props.event.showAt)
        }
      ]
    })

    const project = computed({
      get: () => {
        return state.project.projects.find(p => p.id === props.event.event.projectId)
      },
      set: (value) => {
        const project = value ? {
          ...value,
          bookId: value.book.id
        } : null

        dispatch('updateEvent', {
          id: props.event.id,
          projectId: value?.id || null,
          bookId: props.event.event.bookId
        }).then(() => getUpcoming())

        commit('updateEvent', {
          id: props.event.id,
          event: {
            ...props.event.event,
            project: project
          }
        })
      }
    })

    const time = computed(() => {
      const timeStart = props.event.event.timeStart?.substring(0, 5)
      const timeEnd = props.event.event.timeEnd?.substring(0, 5)

      return timeEnd ? `${timeStart}-${timeEnd}` : timeStart || null
    })

    const url = computed(() => {
      const path = isTeamAccount.value ? `/${team.value.link}` : ''

      return `${path}/space/${project.value.bookId}?page=${project.value.id}`
    })

    const showSelectBookModal = computed(() => props.selectedBooks.length > 1 || isEdit.value)

    async function toggleBook (book) {
      if (props.event.event.eventTags.length > 0) {
        for (const tag of props.event.event.eventTags) {
          await dispatch('toggleEventTag', {
            tag: tag,
            eventId: props.event.event.id,
            isSelected: true,
            bookId: props.event.event.bookId
          })
        }
      }

      dispatch('updateEvent', {
        id: props.event.id,
        newBookId: book.id,
        bookId: props.event.event.bookId
      }).then(() => {
        dispatch('getEventsByDate', {
          startDate: props.firstWeekDate,
          finishDate: props.lastWeekDate,
          bookIds: props.selectedBooks.map(b => b.id)
        }).finally(_ => commit('toggleBookLoading', false))
        getUpcoming()
      })

      commit('updateEvent', {
        id: props.event.id,
        bookId: book.id,
        event: {
          ...props.event.event,
          bookId: book.id
        }
      })
    }

    const toggleEdit = (value) => {
      setTimeout(() => {
        isEdit.value = true
        nextTick(_ => {
          titleInputRef.value.focus()
          titleInputRef.value.setSelectionRange(0, 9999)
        })
      }, 100)
    }

    function deleteEvent () {
      if (props.event.event.periodType && props.event.event.periodValue > 0) {
        recurringModalRef.value.open()
      } else {
        dispatch('deleteEvent', {
          id: props.event.id,
          bookId: props.event.event.bookId
        })
      }
    }

    function confirm (value) {
      deleteRecurring(value)
    }

    function cancelEdit () {
      isEdit.value = false
      save()
    }

    function onBlur () {
      if (!showEmojiPicker.value) {
        save()
      }
    }

    function save () {
      const title = localEvent.value.event.title.trim()
      const subTitle = localEvent.value.event.subTitle?.trim()

      if (title === '') {
        commit('setNotification', {
          isVisible: true,
          message: 'Title cannot be empty'
        })
        localEvent.value.event.title = temporaryTitle.value
        return
      }

      if (title !== temporaryTitle.value.trim() || subTitle !== temporarySubTitle.value || timeWasUpdated) {
        temporaryTitle.value = localEvent.value.event.title
        temporarySubTitle.value = localEvent.value.event.subTitle

        const data = {
          title: title,
          subTitle: subTitle,
          timeStart: localEvent.value.event.timeStart,
          timeEnd: localEvent.value.event.timeEnd,
          bookId: props.event.event.bookId
        }

        dispatch('updateEvent', {
          id: props.event.id,
          ...data
        }).then(() => getUpcoming())

        commit('updateEvent', {
          id: props.event.id,
          event: {
            ...props.event.event,
            ...data
          }
        })
      }
    }

    function setTime ({ timeStart, timeEnd }) {
      if (localEvent.value.event.timeStart !== timeStart || localEvent.value.event.timeEnd !== timeEnd) {
        localEvent.value.event.timeStart = timeStart
        localEvent.value.event.timeEnd = timeEnd
        timeWasUpdated = true
      }
    }

    function getUpcoming () {
      if (props.event.event.periodValue && props.event.event.periodType) {
        dispatch('getUpcomingEvents', {
          startDate: props.lastWeekDate,
          limit: 50,
          bookIds: props.selectedBooks.map(b => b.id)
        })
      }
    }

    function toggleEventTag ({ tag, isSelected }) {
      dispatch('toggleEventTag', {
        tag: tag,
        eventId: props.event.event.id,
        isSelected: isSelected,
        bookId: props.event.event.bookId
      }).then(() => getUpcoming())
    }

    function updateTag ({ name, id }) {
      dispatch('updateEventTag', { name: name, id, bookId: props.event.event.bookId })
        .then(() => {
          dispatch('getEventsByDate', {
            startDate: props.firstWeekDate,
            finishDate: props.lastWeekDate,
            bookIds: props.selectedBooks.map(b => b.id)
          }).finally(_ => commit('toggleBookLoading', false))

          dispatch('getUpcomingEvents', {
            startDate: props.lastWeekDate,
            limit: 50,
            bookIds: props.selectedBooks.map(b => b.id)
          })
        })
    }

    function addRecurring (val) {
      dispatch('addRecurring', {
        startDate: props.event.showAt,
        eventId: props.event.eventId,
        periodType: val.type,
        periodValue: val.value,
        lastWeekDate: props.lastWeekDate,
        bookId: props.event.event.bookId,
        bookIds: props.selectedBooks.map(b => b.id)
      })

      commit('updateEvent', {
        id: props.event.id,
        event: {
          ...props.event.event,
          periodType: val.type,
          periodValue: val.value
        }
      })
    }

    function deleteRecurring (value) {
      const data = {
        lastWeekDate: props.lastWeekDate,
        startDate: props.event.showAt,
        eventId: props.event.eventId,
        id: props.event.id,
        bookId: props.event.event.bookId,
        bookIds: props.selectedBooks.map(b => b.id)
      }

      if (value === 'All events') {
        data.isAll = true
        dispatch('deleteRecurring', data)
      } else if (value === 'This and following events') {
        data.isFuture = true
        dispatch('deleteRecurring', data)
      } else {
        dispatch('deleteEvent', {
          id: props.event.id,
          bookId: props.event.event.bookId
        })
      }
    }

    function updateRecurring ({ period, value }) {
      const data = {
        firstWeekDate: props.firstWeekDate,
        lastWeekDate: props.lastWeekDate,
        startDate: props.event.showAt,
        eventId: props.event.eventId,
        periodValue: period.value,
        periodType: period.type,
        id: props.event.id,
        bookId: props.event.event.bookId,
        bookIds: props.selectedBooks.map(b => b.id)
      }

      if (value === 'All events') {
        data.isAll = true
      } else if (value === 'This and following events') {
        data.isFuture = true
      }

      dispatch('updateRecurring', data)

      commit('updateEvent', {
        id: props.event.id,
        event: {
          ...props.event.event,
          periodType: period.type,
          periodValue: period.value
        }
      })
    }

    function closeEmojiPicker () {
      showEmojiPicker.value = false
    }

    let focusedInput = ''

    function onKeyPress (e) {
      if (e.keyCode === 58) {
        focusedInput = e.target.id
        showEmojiPicker.value = true
      } else {
        focusedInput = ''
        showEmojiPicker.value = false
      }
    }

    function insertEmoji (emoji) {
      if (focusedInput === 'title') {
        titleInputRef.value.focus()
        if (localEvent.value.event.title.length >= 40) return
      } else {
        subTitleInputRef.value.focus()
        if (localEvent.value.event.subTitle.length >= 100) return
      }

      if (localEvent.value.event[focusedInput].slice(-1) === ':') {
        localEvent.value.event[focusedInput] = localEvent.value.event[focusedInput].slice(0, -1) + emoji.char
      } else {
        localEvent.value.event[focusedInput] += emoji.char
      }
    }

    function dayclick (item) {
      dispatch('updateEvent', {
        id: localEvent.value.id,
        order: 9999,
        startDate: item.id,
        showAt: item.id,
        bookId: props.event.event.bookId
      }).then(() => getUpcoming())

      commit('updateEvent', {
        id: localEvent.value.id,
        order: 9999,
        startDate: item.id,
        showAt: item.id
      })
    }

    return {
      // refs
      eventRef,
      titleInputRef,
      subTitleInputRef,
      recurringModalRef,
      selectedBookModalRef,
      // data
      showMenu,
      localEvent,
      showEmojiPicker,
      // computeds
      url,
      time,
      tags,
      isEdit,
      project,
      hasTags,
      allBooks,
      isMobile,
      attributes,
      isDarkMode,
      computedDay,
      selectedBook,
      isTwilightTheme,
      showSelectBookModal,
      // functions
      onBlur,
      confirm,
      setTime,
      dayclick,
      toggleEventTag,
      updateTag,
      toggleBook,
      toggleEdit,
      cancelEdit,
      onKeyPress,
      insertEmoji,
      deleteEvent,
      addRecurring,
      updateRecurring,
      deleteRecurring,
      closeEmojiPicker
    }
  }
})
</script>

<style>
.bar-today::after {
  content: 'Today';
  font-size: 9px;
  position: absolute;
  color: var(--link-color);
  bottom: 0;
  font-weight: 700;
}
</style>
